/**
 * The check an instrument added to a quantity composition passes before it is priced: the
 * engine's identification and validity run, answered with the price where there is no issue.
 * The only place in the tree that calls `resolveNewInstruments` or
 * `resolveNewInstrumentsOfNewPortfolio` — deleting `new-instruments/` takes the call sites with it.
 * Batching and which of the two to ask live in `./resolve`.
 */
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	type InstrumentsResolutionRequest,
	InvestmentSharesControllerV4ApiFactory,
	type NewPortfolioInstrumentsResolutionRequest,
	type ResolvedInstrument,
} from "@eu.mdotm.api/sphere";

const { newAction } = repositoryHelperFactory("newInstrumentChecks");

const sharesApi = () => getApiGen(InvestmentSharesControllerV4ApiFactory);

export const newInstrumentChecksRepository = {
	/** Checks `request.instruments` for the portfolio at `request.referenceDate`. Synchronous and slow. */
	resolveNewInstruments: newAction(
		async (investmentUuid: string, request: InstrumentsResolutionRequest): Promise<ResolvedInstrument[]> =>
			(await axiosExtract(sharesApi().resolveNewInstruments(investmentUuid, request))).instruments ?? [],
	),
	/**
	 * Checks `request.instruments` for a portfolio not saved yet, at today, priced in
	 * `request.currency`. Synchronous and slow.
	 */
	resolveNewInstrumentsOfNewPortfolio: newAction(
		async (request: NewPortfolioInstrumentsResolutionRequest): Promise<ResolvedInstrument[]> =>
			(await axiosExtract(sharesApi().resolveNewInstrumentsOfNewPortfolio(request))).instruments ?? [],
	),
} as const;
